import { IdentityVerificationContent } from "@/components/Account/IdentityVerification/IdentityVerificationContent";
import {
  BadgeCheck,
  CarFront,
  CreditCard,
  FileCheck2,
  ShieldCheck,
} from "lucide-react";

type VerificationSection = {
  title: string;
  subtitle: string;
  description: string;
  reviewTime: string;
  checks: string[];
  icon: typeof CreditCard;
};

const verificationSections: VerificationSection[] = [
  {
    title: "Identification Documents",
    subtitle: "Aadhaar, PAN, passport, voter ID, and similar proofs",
    description:
      "We review the uploaded identity proof to make sure the document is readable, the details appear valid, and the information matches the profile submitted on the platform.",
    reviewTime: "Usually reviewed within 24 hours",
    checks: [
      "Name, document number, and profile details are compared",
      "Front and back uploads are checked for readability",
      "Blurred, cropped, or mismatched documents are flagged for resubmission",
    ],
    icon: CreditCard,
  },
  {
    title: "Driver License Verification",
    subtitle: "Driver eligibility and visible license validity checks",
    description:
      "For drivers, we perform a separate review of the driving license to confirm the holder details, visible expiry date, and whether the submitted license is suitable for onboarding.",
    reviewTime: "Usually reviewed within 24 to 48 hours",
    checks: [
      "License holder name is matched with the account identity",
      "Expiry date and visible license information are reviewed",
      "Accounts may remain pending until clearer files are uploaded",
    ],
    icon: BadgeCheck,
  },
  {
    title: "Vehicle Registration",
    subtitle: "RC and vehicle ownership-related details",
    description:
      "Vehicle registration verification helps us confirm that the submitted vehicle details are consistent with the registration document and the vehicle information provided by the driver.",
    reviewTime: "Usually reviewed within 48 hours",
    checks: [
      "Registration number, owner name, and vehicle details are cross-checked",
      "Vehicle make, model, and plate information are reviewed",
      "Missing or unclear registration files are sent back for correction",
    ],
    icon: CarFront,
  },
];

const reviewSteps = [
  {
    title: "Upload",
    description:
      "The user submits identity, driver, or vehicle documents through the verification flow.",
  },
  {
    title: "Quality Check",
    description:
      "Our system checks file clarity, completeness, and whether the uploaded format can be reviewed.",
  },
  {
    title: "Manual Review",
    description:
      "A reviewer validates the important details and compares them with the account data.",
  },
  {
    title: "Status Update",
    description:
      "The document is marked approved, pending clarification, or rejected for re-upload.",
  },
];

const VerificationProcess = () => {
  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,#fff7e8_0%,#fffdf7_42%,#f7f4ef_100%)] py-10">
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-8 px-4 sm:px-6 lg:px-8">
        <section className="overflow-hidden rounded-[28px] border border-[#ca4015]/10 bg-white shadow-[0_16px_50px_rgba(33,24,14,0.08)]">
          <div className="bg-[radial-gradient(circle_at_top_left,_rgba(202,64,21,0.16),_transparent_34%),linear-gradient(135deg,#fff8ee_0%,#ffffff_100%)] p-6 sm:p-8 lg:p-10">
            

            <div className="mt-5 max-w-3xl">
              <h1 className="font-roboto text-3xl font-bold text-gray-950 sm:text-4xl">
                Verification Process
              </h1>
              <p className="mt-4 text-sm leading-7 text-gray-700 sm:text-base">
                This page explains how we review identification documents,
                driver licenses, and vehicle registration details before an
                account is fully verified. The content below is sample copy for
                the public route and can be replaced with live business rules
                later.
              </p>
            </div>

            <div className="mt-8 grid gap-4 sm:grid-cols-3">
              <div className="rounded-2xl border border-white/70 bg-white/80 p-4 backdrop-blur">
                <div className="flex items-center gap-3">
                  <div className="rounded-xl bg-[#ca4015]/10 p-2 text-[#ca4015]">
                    <ShieldCheck className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">
                      Secure Review
                    </p>
                    <p className="text-xs text-gray-600">
                      Submitted files go through a protected review flow
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-white/70 bg-white/80 p-4 backdrop-blur">
                <div className="flex items-center gap-3">
                  <div className="rounded-xl bg-blue-100 p-2 text-blue-700">
                    <FileCheck2 className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">
                      Layered Checks
                    </p>
                    <p className="text-xs text-gray-600">
                      Files are checked for clarity, matching details, and completeness
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-white/70 bg-white/80 p-4 backdrop-blur">
                <div className="flex items-center gap-3">
                  <div className="rounded-xl bg-amber-100 p-2 text-amber-700">
                    <BadgeCheck className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">
                      Status Updates
                    </p>
                    <p className="text-xs text-gray-600">
                      Every document moves through pending, approved, or re-upload states
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="grid gap-5 lg:grid-cols-3">
          {verificationSections.map((section) => {
            const Icon = section.icon;

            return (
              <article
                key={section.title}
                className="rounded-[24px] border border-gray-200 bg-white p-6 shadow-sm transition-transform duration-200 hover:-translate-y-1"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="rounded-2xl bg-[#ca4015]/10 p-3 text-[#ca4015]">
                    <Icon className="h-6 w-6" />
                  </div>
                  <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600">
                    {section.reviewTime}
                  </span>
                </div>

                <h2 className="mt-5 text-xl font-bold text-gray-900">
                  {section.title}
                </h2>
                <p className="mt-1 text-sm font-medium text-[#ca4015]">
                  {section.subtitle}
                </p>
                <p className="mt-4 text-sm leading-7 text-gray-600">
                  {section.description}
                </p>

                <div className="mt-5 space-y-3">
                  {section.checks.map((check) => (
                    <div key={check} className="flex items-start gap-3">
                      <span className="mt-1 h-2.5 w-2.5 rounded-full bg-[#ca4015]" />
                      <p className="text-sm leading-6 text-gray-700">{check}</p>
                    </div>
                  ))}
                </div>
              </article>
            );
          })}
        </section>

        <section className="rounded-[28px] border border-blue-200 bg-[linear-gradient(135deg,#eff6ff_0%,#f8fbff_100%)] p-6 shadow-sm sm:p-8">
          <h2 className="text-2xl font-bold text-blue-950">
            How The Review Flow Works
          </h2>
          <p className="mt-3 max-w-3xl text-sm leading-7 text-blue-900">
            Once documents are submitted, they move through a basic review
            sequence. This is placeholder content that explains the journey in a
            simple way for visitors who open the public route.
          </p>

          <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {reviewSteps.map((step, index) => (
              <div
                key={step.title}
                className="rounded-2xl border border-blue-100 bg-white/80 p-5"
              >
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-600">
                  Step {index + 1}
                </p>
                <h3 className="mt-2 text-lg font-semibold text-gray-900">
                  {step.title}
                </h3>
                <p className="mt-2 text-sm leading-6 text-gray-600">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </section>

        
      </div>
    </div>
  );
};

export default VerificationProcess;
